// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import {
  defaultDoughnutOptions,
  chartColorAlpha,
  chartColor,
} from './loadChart';

function buildCategoryData(transactions) {
  if (!transactions?.length) return null;

  const totals = {};
  transactions.forEach((t) => {
    if (t.transaction_type !== 'EXPENSE') return;
    // Use category name from category_detail, fallback to 'Other'
    const catName = t.category_detail?.name || 'Other';
    totals[catName] = (totals[catName] ?? 0) + Math.abs(parseFloat(t.amount) || 0);
  });

  const labels = Object.keys(totals);
  if (!labels.length) return null;

  return {
    labels,
    datasets: [
      {
        label: 'Expenses by Category',
        data: labels.map((l) => totals[l]),
        backgroundColor: labels.map((_, i) => chartColorAlpha(i, 0.8)),
        borderColor: labels.map((_, i) => chartColor(i)),
        borderWidth: 2,
      },
    ],
  };
}

const SAMPLE_DATA = {
  labels: ['Food', 'Transport', 'Entertainment', 'Shopping', 'Utilities'],
  datasets: [
    {
      label: 'Expenses by Category',
      data: [450, 320, 180, 250, 150],
      backgroundColor: [0, 1, 2, 3, 4].map((i) => chartColorAlpha(i, 0.8)),
      borderColor: [0, 1, 2, 3, 4].map((i) => chartColor(i)),
      borderWidth: 2,
    },
  ],
};

export default function ProfileChart({ data = null }) {
  const chartData = useMemo(
    () => buildCategoryData(data) ?? SAMPLE_DATA,
    [data],
  );

  return (
    <div className="h-64 w-full">
      <Doughnut
        data={chartData}
        options={defaultDoughnutOptions('Expense Categories')}
      />
    </div>
  );
}
